import { useMemo, useRef, useState } from 'react';

// Mui
import { Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, TextField, Tooltip, useTheme } from '@mui/material';
import { InputAdornment } from '@mui/material';

// Icons
import { CheckCircleTwoTone, DeleteOutlined, SearchOutlined } from '@ant-design/icons';

// Utils
import { findKey } from 'lodash';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { capitalizeEachWord, getErrorMessage } from 'utils/helper';

// types
import { UserRole } from 'types/auth';

// Components
import IconButton from '../@extended/IconButton';
import ConfirmDialog, { DialogAction } from '../dialogs/ConfirmDialog';
import Spinner from '../Spinner';

// Redux
import { useGetUserRolesQuery, useRemoveUserRoleMutation } from 'store/reducers/api/user';

// Hooks
import useSnackbar from 'hooks/useSnackbar';
import useAuth from 'hooks/useAuth';
import useEnums from 'hooks/useEnum';

export interface UserRolesListProps {
  userId: number;
  showDeleteButton?: boolean;
  showActiveRole?: boolean;
  isItemButton?: boolean;
  onSelectRole?: (userRole: UserRole) => void;
  showSearch?: boolean;
  forbiddenRoleIds?: number[];
  view?: 'default' | 'po';
}

function UserRolesList({
  userId,
  showDeleteButton,
  showActiveRole,
  isItemButton,
  onSelectRole,
  showSearch,
  forbiddenRoleIds,
  view = 'default'
}: UserRolesListProps) {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { activeRole } = useAuth();
  const { UserRolesEnum } = useEnums();

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  /** holding id to remove */
  const removeId = useRef<number>();

  const { data: userRoles, isLoading: isFetching } = useGetUserRolesQuery(userId, { refetchOnMountOrArgChange: true });
  const [removeUserRole, { isLoading: isRemovingRole }] = useRemoveUserRoleMutation();

  const isLoading = isFetching || isRemovingRole;

  const roles = useMemo(
    () => (userRoles || []).filter(({ user_role_type_id }) => !forbiddenRoleIds?.includes(user_role_type_id)),
    [userRoles, forbiddenRoleIds]
  );

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    removeId.current = undefined;
  };

  const handleRemove = async () => {
    setOpenConfirmDialog(false);
    if (!removeId.current) return;

    if (roles.length === 1) {
      snackbar.showError('User must have at least one role');
      return;
    }

    try {
      await removeUserRole({ roleId: removeId.current }).unwrap();
      snackbar.showSuccess('Deleted successfully');
    } catch (error) {
      snackbar.showError(getErrorMessage(error));
    }
  };

  const actions: Record<DialogAction, () => void> = {
    ok: handleRemove,
    cancel: handleCloseConfirmDialog
  };

  const handleDialogAction = (value: DialogAction) => {
    actions[value]?.();
  };

  const handleClickOnRemove = (id: number) => {
    removeId.current = id;
    setOpenConfirmDialog(true);
  };

  const filteredRoles = useMemo(() => {
    const formattedRoles = roles.flatMap(({ company, property, building, unit, ...userRole }) => {
      const roleNameKey = findKey(UserRolesEnum, (value) => value === userRole.user_role_type_id);
      const roleName = roleNameKey ? capitalizeEachWord(roleNameKey) : '';

      let roleDescription = roleName;

      const isStandardRole =
        userRole.user_role_type_id > UserRolesEnum.SYSTEM_SUPPORT && userRole.user_role_type_id !== UserRolesEnum.SENSOR_TESTER;

      if (isStandardRole) {
        const targets = view === 'po' ? [property, company] : [company, property, building, unit];

        const locationNames = targets
          .filter(Boolean)
          .map((item) => item!.name)
          .join(', ');

        if (locationNames) {
          roleDescription = view === 'po' ? locationNames : `${roleName} of ${locationNames}`;
        }
      }

      if (!roleDescription) return [];

      return [{ roleDescription, userRole }];
    });

    if (!searchKeyword) return formattedRoles;

    return formattedRoles.filter(({ roleDescription }) => roleDescription.toLowerCase().includes(searchKeyword.toLowerCase()));
  }, [roles, searchKeyword, UserRolesEnum, view]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', margin: '0 -20px', padding: '0 20px' }}>
      {isLoading && <Spinner />}
      <ConfirmDialog open={openConfirmDialog} onClose={handleCloseConfirmDialog} onAction={handleDialogAction} />

      {showSearch && (
        <Box
          sx={{
            position: 'sticky',
            top: 1,
            zIndex: 1,
            backgroundColor: '#fff',
            padding: 1,
            borderRadius: 2,
            boxShadow: '0 1px 6px 0 rgba(32, 33, 36, 0.28);'
          }}
        >
          <TextField
            value={searchKeyword}
            onChange={({ currentTarget: { value } }) => setSearchKeyword(value)}
            fullWidth
            sx={{ padding: 0 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: -0.5 }}>
                  <IconButton size="small">
                    <SearchOutlined style={{ fontSize: 'inherit' }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            placeholder="Type to search..."
          />
        </Box>
      )}

      <List>
        {filteredRoles.map(({ userRole, roleDescription }) => {
          const showUserActiveRole = showActiveRole && activeRole?.id === userRole.id && userId === userRole.user_id;
          const matches = match(roleDescription, searchKeyword, { insideWords: true });
          const parts = parse(roleDescription, matches);

          const ListContent = (
            <>
              {showDeleteButton && (
                <ListItemAvatar>
                  <IconButton color="error" onClick={() => handleClickOnRemove(userRole.id)}>
                    <DeleteOutlined />
                  </IconButton>
                </ListItemAvatar>
              )}
              <ListItemText
                primary={parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}
              />
            </>
          );

          return (
            <ListItem
              divider={filteredRoles.length > 1}
              key={userRole.id}
              disablePadding={isItemButton}
              {...(showUserActiveRole && {
                secondaryAction: (
                  <Tooltip title="Active Role">
                    <CheckCircleTwoTone twoToneColor={theme.palette.success.main} />
                  </Tooltip>
                )
              })}
            >
              {isItemButton ? <ListItemButton onClick={() => onSelectRole?.(userRole)}>{ListContent}</ListItemButton> : ListContent}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

export default UserRolesList;
